<template>
  <div class="media-player">
    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <b-loading :is-full-page="false" :active="true" />
      <p class="loading-text">Loading media...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <b-message type="is-danger" has-icon>
        <strong>Playback Error</strong>
        <p>{{ error }}</p>
        <p class="help">{{ errorHelp }}</p>
      </b-message>
    </div>

    <!-- Video Player -->
    <div v-else-if="signedUrl" class="player-container">
      <video
        ref="videoPlayer"
        :src="signedUrl"
        :controls="false"
        :autoplay="autoplay"
        :preload="preload"
        :poster="poster"
        class="video-element"
        @error="handleVideoError"
        @loadstart="onLoadStart"
        @loadeddata="onLoadedData"
        @canplay="onCanPlay"
        @play="onPlay"
        @pause="onPause"
        @ended="onEnded"
        @timeupdate="onTimeUpdate"
        @durationchange="onDurationChange"
      >
        <source :src="signedUrl" :type="mimeType" />
        Your browser does not support the video tag.
      </video>

      <!-- Custom Transport Controls with Timecode -->
      <div class="custom-controls">
        <!-- Progress Bar -->
        <div class="progress-container" @click="seekToPosition" ref="progressBar">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: progressPercent + '%' }"></div>
            <div class="progress-handle" :style="{ left: progressPercent + '%' }"></div>
          </div>
        </div>

        <!-- Controls Row -->
        <div class="controls-row">
          <!-- Left: Transport Controls -->
          <div class="transport-controls">
            <!-- Frame Back -->
            <b-button
              type="is-dark"
              size="is-small"
              title="Previous Frame (←)"
              @click="stepBackward"
            >
              <span class="icon"><i class="mdi mdi-skip-previous"></i></span>
              <span class="btn-label">-1f</span>
            </b-button>
            <!-- 1 Second Back -->
            <b-button
              type="is-dark"
              size="is-small"
              title="Back 1 Second (Shift+←)"
              @click="seekBackward(1)"
            >
              <span class="icon"><i class="mdi mdi-rewind"></i></span>
              <span class="btn-label">-1s</span>
            </b-button>
            <!-- Play/Pause -->
            <b-button
              :type="isPlaying ? 'is-warning' : 'is-success'"
              size="is-small"
              :title="isPlaying ? 'Pause (Space)' : 'Play (Space)'"
              @click="togglePlay"
            >
              <span class="icon"><i :class="isPlaying ? 'mdi mdi-pause' : 'mdi mdi-play'"></i></span>
            </b-button>
            <!-- 1 Second Forward -->
            <b-button
              type="is-dark"
              size="is-small"
              title="Forward 1 Second (Shift+→)"
              @click="seekForward(1)"
            >
              <span class="btn-label">+1s</span>
              <span class="icon"><i class="mdi mdi-fast-forward"></i></span>
            </b-button>
            <!-- Frame Forward -->
            <b-button
              type="is-dark"
              size="is-small"
              title="Next Frame (→)"
              @click="stepForward"
            >
              <span class="btn-label">+1f</span>
              <span class="icon"><i class="mdi mdi-skip-next"></i></span>
            </b-button>
            <!-- Stop (Go to beginning) -->
            <b-button
              type="is-dark"
              size="is-small"
              title="Stop & Reset (S)"
              @click="stop"
            >
              <span class="icon"><i class="mdi mdi-stop"></i></span>
            </b-button>
          </div>

          <!-- Center: Timecode Display -->
          <div class="timecode-display">
            <span class="timecode-current">{{ currentTimecode }}</span>
            <span class="timecode-separator">/</span>
            <span class="timecode-duration">{{ durationTimecode }}</span>
          </div>

          <!-- Right: Additional Controls -->
          <div class="additional-controls">
            <!-- Speed Control -->
            <b-dropdown
              position="is-top-right"
              :mobile-modal="false"
              aria-role="menu"
            >
              <template #trigger>
                <b-button
                  type="is-dark"
                  size="is-small"
                  title="Playback Speed"
                >
                  <span class="icon"><i class="mdi mdi-speedometer"></i></span>
                  <span class="btn-label">{{ playbackSpeed }}x</span>
                </b-button>
              </template>
              <b-dropdown-item
                v-for="speed in playbackSpeeds"
                :key="speed"
                @click="setPlaybackSpeed(speed)"
                :class="{ 'is-active': playbackSpeed === speed }"
              >
                {{ speed }}x
              </b-dropdown-item>
            </b-dropdown>
            <!-- Fullscreen -->
            <b-button
              type="is-dark"
              size="is-small"
              title="Fullscreen (F)"
              @click="toggleFullscreen"
            >
              <span class="icon"><i class="mdi mdi-fullscreen"></i></span>
            </b-button>
          </div>
        </div>

        <!-- Frame Rate Info -->
        <div class="framerate-info">
          <span class="framerate-label">Frame Rate:</span>
          <span class="framerate-value">{{ frameRate }} fps</span>
          <span class="frame-indicator">Frame: {{ currentFrame }}</span>
        </div>
      </div>

      <!-- Media Info -->
      <div v-if="showInfo" class="media-info">
        <div class="info-row">
          <span class="info-label">File:</span>
          <span class="info-value">{{ sourceFilename }}</span>
        </div>
        <div v-if="mediaTitle" class="info-row">
          <span class="info-label">Title:</span>
          <span class="info-value">{{ mediaTitle }}</span>
        </div>
        <div v-if="provider" class="info-row">
          <span class="info-label">Source:</span>
          <b-tag :type="getProviderTagType(provider)">{{
            provider.toUpperCase()
          }}</b-tag>
        </div>
        <div v-if="expiresIn" class="info-row">
          <span class="info-label">URL Expires:</span>
          <span class="info-value">{{ formatExpiry(expiresIn) }}</span>
        </div>
      </div>
    </div>

    <!-- No Storage Path -->
    <div v-else class="no-media-container">
      <b-message type="is-info" has-icon>
        <p>No storage path configured for this media.</p>
        <p class="help">Add a storage path to enable media playback.</p>
      </b-message>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'
import { getCookie } from '@/cookies-helper'

export default {
  name: 'MediaPlayer',
  props: {
    // Option 1: Provide storagePath and let component generate presigned URL
    storagePath: {
      type: String,
      required: false,
      default: null
    },
    // Storage config ID - essential for paths that don't include bucket name
    storageConfigId: {
      type: [Number, String],
      required: false,
      default: null
    },
    // Option 2: Provide a direct URL (e.g., already-generated presigned URL)
    url: {
      type: String,
      required: false,
      default: null
    },
    sourceFilename: {
      type: String,
      required: false,
      default: ''
    },
    mediaTitle: {
      type: String,
      required: false,
      default: ''
    },
    autoplay: {
      type: Boolean,
      default: false
    },
    controls: {
      type: Boolean,
      default: true
    },
    preload: {
      type: String,
      default: 'metadata',
      validator: value => ['none', 'metadata', 'auto'].includes(value)
    },
    poster: {
      type: String,
      default: null
    },
    showInfo: {
      type: Boolean,
      default: true
    },
    // Frame rate for timecode calculation (default 25fps for PAL/broadcast)
    frameRate: {
      type: Number,
      default: 25
    }
  },
  data() {
    return {
      signedUrl: null,
      mimeType: 'video/mp4',
      expiresIn: null,
      provider: null,
      loading: false,
      error: null,
      errorHelp: null,
      // Playback state
      currentTime: 0,
      duration: 0,
      isPlaying: false,
      playbackSpeed: 1,
      playbackSpeeds: [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2]
    }
  },
  computed: {
    // Calculate frame duration in seconds
    frameDuration() {
      return 1 / this.frameRate
    },
    // Current frame number
    currentFrame() {
      return Math.floor(this.currentTime * this.frameRate)
    },
    // Total frame count
    totalFrames() {
      return Math.floor(this.duration * this.frameRate)
    },
    // Progress percentage for scrubber
    progressPercent() {
      if (!this.duration) return 0
      return (this.currentTime / this.duration) * 100
    },
    // Current timecode in HH:MM:SS:FF format
    currentTimecode() {
      return this.secondsToTimecode(this.currentTime)
    },
    // Duration timecode in HH:MM:SS:FF format
    durationTimecode() {
      return this.secondsToTimecode(this.duration)
    }
  },
  watch: {
    storagePath: {
      immediate: true,
      handler(newPath) {
        if (newPath && !this.url) {
          this.generatePlaybackUrl()
        }
      }
    },
    url: {
      immediate: true,
      handler(newUrl) {
        if (newUrl) {
          // Use direct URL - no need to generate presigned URL
          this.signedUrl = newUrl
          this.provider = 'url'
        }
      }
    }
  },
  mounted() {
    // Add keyboard event listener for shortcuts
    window.addEventListener('keydown', this.handleKeydown)
  },
  beforeDestroy() {
    // Remove keyboard event listener
    window.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    async generatePlaybackUrl() {
      if (!this.storagePath) {
        return
      }

      try {
        this.loading = true
        this.error = null
        this.errorHelp = null

        const requestBody = {
          storagePath: this.storagePath
        }
        // Include storageConfigId if provided - essential for paths without bucket name
        if (this.storageConfigId) {
          requestBody.storageConfigId = this.storageConfigId
        }
        const response = await axios.post('/api/storage/generate-url', requestBody)

        let playbackUrl = response.data.url

        // For proxy URLs (Wasabi, etc.), prepend API base URL and append auth token
        // This is needed because the video src attribute uses the browser's origin,
        // not the axios baseURL, so we need the full absolute URL
        if (response.data.useProxy) {
          const apiBaseUrl = axios.defaults.baseURL || ''
          const token = getCookie('token')
          const separator = playbackUrl.includes('?') ? '&' : '?'
          playbackUrl = apiBaseUrl + playbackUrl + (token ? `${separator}token=${token}` : '')
          console.log('MediaPlayer: Using proxy URL:', playbackUrl)
        }

        this.signedUrl = playbackUrl
        this.mimeType = response.data.mimeType || 'video/mp4'
        this.expiresIn = response.data.expiresIn
        this.provider = response.data.provider

        this.$emit('url-generated', {
          url: this.signedUrl,
          mimeType: this.mimeType,
          expiresIn: this.expiresIn,
          provider: this.provider
        })
      } catch (err) {
        console.error('Media player error:', err)

        const errorResponse = err.response?.data

        if (errorResponse?.error) {
          this.error = errorResponse.error

          // Provide helpful error messages
          if (errorResponse.error.includes('No storage credentials')) {
            this.errorHelp =
              'Please configure storage credentials in Admin > Storage Settings'
          } else if (errorResponse.error.includes('Invalid storage path')) {
            this.errorHelp =
              'The storage path format is invalid. Expected: s3://bucket/key or /path/to/file'
          } else if (
            errorResponse.error.includes('Access credentials are required')
          ) {
            this.errorHelp =
              'This bucket is private but credentials are missing'
          }
        } else {
          this.error = 'Failed to load media file'
          this.errorHelp =
            'Please check your storage configuration and try again'
        }

        this.$emit('error', {
          error: this.error,
          details: errorResponse
        })
      } finally {
        this.loading = false
      }
    },

    handleVideoError(event) {
      console.error('Video playback error:', event)

      const videoElement = this.$refs.videoPlayer
      const errorCode = videoElement?.error?.code

      let errorMessage = 'Failed to play media file'
      let helpMessage = ''

      switch (errorCode) {
        case 1: // MEDIA_ERR_ABORTED
          errorMessage = 'Media playback was aborted'
          helpMessage =
            'Try refreshing the page or check your network connection'
          break
        case 2: // MEDIA_ERR_NETWORK
          errorMessage = 'Network error while loading media'
          helpMessage = 'Check your internet connection and try again'
          break
        case 3: // MEDIA_ERR_DECODE
          errorMessage = 'Media file is corrupted or unsupported'
          helpMessage = 'The file may be damaged or in an unsupported format'
          break
        case 4: // MEDIA_ERR_SRC_NOT_SUPPORTED
          errorMessage = 'Media format not supported'
          helpMessage = 'Your browser may not support this video format'
          break
        default:
          errorMessage = 'Unknown playback error'
          helpMessage = 'Please try a different browser or contact support'
      }

      this.error = errorMessage
      this.errorHelp = helpMessage

      this.$emit('playback-error', {
        error: errorMessage,
        code: errorCode,
        event
      })
    },

    onLoadStart() {
      this.$emit('loadstart')
    },

    onLoadedData() {
      this.loading = false
      this.$emit('loadeddata')
    },

    onCanPlay() {
      this.$emit('canplay')
    },

    onPlay() {
      this.isPlaying = true
      this.$emit('play')
    },

    onPause() {
      this.isPlaying = false
      this.$emit('pause')
    },

    onEnded() {
      this.isPlaying = false
      this.$emit('ended')
    },

    onTimeUpdate() {
      if (this.$refs.videoPlayer) {
        this.currentTime = this.$refs.videoPlayer.currentTime
        this.$emit('timeupdate', {
          currentTime: this.currentTime,
          timecode: this.currentTimecode,
          frame: this.currentFrame
        })
      }
    },

    onDurationChange() {
      if (this.$refs.videoPlayer) {
        this.duration = this.$refs.videoPlayer.duration
        this.$emit('durationchange', {
          duration: this.duration,
          timecode: this.durationTimecode,
          totalFrames: this.totalFrames
        })
      }
    },

    // Convert seconds to HH:MM:SS:FF timecode format
    secondsToTimecode(seconds) {
      if (!seconds || isNaN(seconds)) return '00:00:00:00'

      const totalFrames = Math.floor(seconds * this.frameRate)
      const frames = totalFrames % this.frameRate
      const totalSeconds = Math.floor(totalFrames / this.frameRate)
      const secs = totalSeconds % 60
      const totalMinutes = Math.floor(totalSeconds / 60)
      const mins = totalMinutes % 60
      const hours = Math.floor(totalMinutes / 60)

      return [
        String(hours).padStart(2, '0'),
        String(mins).padStart(2, '0'),
        String(secs).padStart(2, '0'),
        String(frames).padStart(2, '0')
      ].join(':')
    },

    // Toggle play/pause
    togglePlay() {
      if (this.$refs.videoPlayer) {
        if (this.isPlaying) {
          this.$refs.videoPlayer.pause()
        } else {
          this.$refs.videoPlayer.play()
        }
      }
    },

    // Step forward one frame
    stepForward() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
        const newTime = Math.min(
          this.$refs.videoPlayer.currentTime + this.frameDuration,
          this.duration
        )
        this.$refs.videoPlayer.currentTime = newTime
        this.currentTime = newTime
      }
    },

    // Step backward one frame
    stepBackward() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
        const newTime = Math.max(
          this.$refs.videoPlayer.currentTime - this.frameDuration,
          0
        )
        this.$refs.videoPlayer.currentTime = newTime
        this.currentTime = newTime
      }
    },

    // Seek forward by specified seconds
    seekForward(seconds) {
      if (this.$refs.videoPlayer) {
        const newTime = Math.min(
          this.$refs.videoPlayer.currentTime + seconds,
          this.duration
        )
        this.$refs.videoPlayer.currentTime = newTime
        this.currentTime = newTime
      }
    },

    // Seek backward by specified seconds
    seekBackward(seconds) {
      if (this.$refs.videoPlayer) {
        const newTime = Math.max(
          this.$refs.videoPlayer.currentTime - seconds,
          0
        )
        this.$refs.videoPlayer.currentTime = newTime
        this.currentTime = newTime
      }
    },

    // Seek to position based on click on progress bar
    seekToPosition(event) {
      if (this.$refs.videoPlayer && this.$refs.progressBar && this.duration) {
        const rect = this.$refs.progressBar.getBoundingClientRect()
        const clickX = event.clientX - rect.left
        const percent = clickX / rect.width
        const newTime = percent * this.duration
        this.$refs.videoPlayer.currentTime = Math.max(0, Math.min(newTime, this.duration))
        this.currentTime = this.$refs.videoPlayer.currentTime
      }
    },

    // Set playback speed
    setPlaybackSpeed(speed) {
      this.playbackSpeed = speed
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.playbackRate = speed
      }
    },

    // Handle keyboard shortcuts
    handleKeydown(event) {
      // Only handle if player is focused or no other input is focused
      const activeElement = document.activeElement
      if (activeElement && (activeElement.tagName === 'INPUT' || activeElement.tagName === 'TEXTAREA')) {
        return
      }

      switch (event.key) {
        case ' ':
        case 'k':
          event.preventDefault()
          this.togglePlay()
          break
        case 'ArrowLeft':
          event.preventDefault()
          if (event.shiftKey) {
            this.seekBackward(1)
          } else {
            this.stepBackward()
          }
          break
        case 'ArrowRight':
          event.preventDefault()
          if (event.shiftKey) {
            this.seekForward(1)
          } else {
            this.stepForward()
          }
          break
        case 's':
        case 'S':
          event.preventDefault()
          this.stop()
          break
        case 'f':
        case 'F':
          event.preventDefault()
          this.toggleFullscreen()
          break
        case 'j':
          event.preventDefault()
          this.seekBackward(10)
          break
        case 'l':
          event.preventDefault()
          this.seekForward(10)
          break
      }
    },

    getProviderTagType(provider) {
      const types = {
        s3: 'is-info',
        local: 'is-success',
        nas: 'is-warning',
        url: 'is-link'
      }
      return types[provider] || 'is-light'
    },

    formatExpiry(seconds) {
      if (!seconds) return 'Never'

      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)

      if (hours > 0) {
        return `${hours}h ${minutes}m`
      } else {
        return `${minutes} minutes`
      }
    },

    // Public methods
    play() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.play()
      }
    },

    pause() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
      }
    },

    stop() {
      if (this.$refs.videoPlayer) {
        this.$refs.videoPlayer.pause()
        this.$refs.videoPlayer.currentTime = 0
      }
    },

    toggleFullscreen() {
      if (this.$refs.videoPlayer) {
        if (this.$refs.videoPlayer.requestFullscreen) {
          this.$refs.videoPlayer.requestFullscreen()
        } else if (this.$refs.videoPlayer.webkitRequestFullscreen) {
          this.$refs.videoPlayer.webkitRequestFullscreen()
        } else if (this.$refs.videoPlayer.mozRequestFullScreen) {
          this.$refs.videoPlayer.mozRequestFullScreen()
        }
      }
    }
  }
}
</script>

<style scoped>
.media-player {
  width: 100%;
  margin: 1rem 0;
}

.loading-container {
  min-height: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f5f5f5;
  border-radius: 4px;
  padding: 2rem;
}

.loading-text {
  margin-top: 1rem;
  color: #7a7a7a;
  font-size: 0.875rem;
}

.error-container,
.no-media-container {
  margin: 1rem 0;
}

.player-container {
  background: #000;
  border-radius: 4px;
  overflow: hidden;
}

.video-element {
  width: 100%;
  max-height: 600px;
  display: block;
  background: #000;
}

.media-info {
  background: #f5f5f5;
  padding: 0.75rem 1rem;
  border-top: 1px solid #dbdbdb;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
}

.info-row:last-child {
  margin-bottom: 0;
}

.info-label {
  font-size: 0.75rem;
  font-weight: 600;
  color: #7a7a7a;
  margin-right: 0.5rem;
  min-width: 80px;
}

.info-value {
  font-size: 0.875rem;
  color: #363636;
  word-break: break-all;
}

/* Custom Transport Controls */
.custom-controls {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0.7));
  padding: 0.5rem 1rem 0.75rem;
}

/* Progress Bar */
.progress-container {
  cursor: pointer;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
}

.progress-bar {
  position: relative;
  height: 6px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
  overflow: visible;
}

.progress-fill {
  height: 100%;
  background: #00d1b2;
  border-radius: 3px;
  transition: width 0.1s linear;
}

.progress-handle {
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 14px;
  height: 14px;
  background: #fff;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  cursor: grab;
  transition: transform 0.1s ease;
}

.progress-handle:hover {
  transform: translate(-50%, -50%) scale(1.2);
}

/* Controls Row */
.controls-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.transport-controls {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.transport-controls .button {
  min-width: 32px;
}

/* Force white icons and text on dark buttons in transport controls - use ::v-deep for scoped styles */
.transport-controls ::v-deep .button.is-dark,
.additional-controls ::v-deep .button.is-dark {
  background-color: rgba(50, 50, 50, 0.9) !important;
  border-color: rgba(80, 80, 80, 0.8) !important;
  color: #ffffff !important;
}

.transport-controls ::v-deep .button.is-dark:hover,
.additional-controls ::v-deep .button.is-dark:hover {
  background-color: rgba(70, 70, 70, 0.95) !important;
  border-color: rgba(100, 100, 100, 0.9) !important;
}

.transport-controls ::v-deep .button .icon,
.additional-controls ::v-deep .button .icon,
.transport-controls ::v-deep .button .icon i,
.additional-controls ::v-deep .button .icon i {
  color: #ffffff !important;
  font-size: 1.1rem;
}

.transport-controls ::v-deep .button .btn-label,
.additional-controls ::v-deep .button .btn-label {
  color: #ffffff !important;
}

/* Also add non-scoped fallback just in case */
.custom-controls .button {
  color: #ffffff !important;
}

.custom-controls .button .icon,
.custom-controls .button .icon i {
  color: #ffffff !important;
}

/* Timecode Display - High visibility on dark background */
.timecode-display {
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 1.1rem;
  font-weight: 700;
  color: #fff;
  background: rgba(0, 0, 0, 0.7);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  letter-spacing: 1px;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
}

.timecode-current {
  color: #4de6d0;
  text-shadow: 0 0 8px rgba(0, 209, 178, 0.5);
}

.timecode-separator {
  color: #ffffff;
  margin: 0 0.5rem;
  opacity: 0.7;
}

.timecode-duration {
  color: #ffffff;
}

/* Button labels for transport controls */
.btn-label {
  font-size: 0.7rem;
  font-weight: 600;
  margin-left: 2px;
  letter-spacing: 0.5px;
}

/* Additional Controls */
.additional-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Frame Rate Info */
.framerate-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}

.framerate-label {
  color: rgba(255, 255, 255, 0.4);
}

.framerate-value {
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
}

.frame-indicator {
  margin-left: auto;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  color: #ffdd57;
}

/* Responsive */
@media screen and (max-width: 768px) {
  .video-element {
    max-height: 400px;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
  }

  .info-label {
    margin-bottom: 0.25rem;
  }

  .controls-row {
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .timecode-display {
    order: -1;
    width: 100%;
    text-align: center;
    margin-bottom: 0.5rem;
  }

  .transport-controls {
    flex: 1;
    justify-content: center;
  }

  .additional-controls {
    flex: 1;
    justify-content: flex-end;
  }

  .framerate-info {
    flex-wrap: wrap;
    justify-content: center;
  }

  .frame-indicator {
    margin-left: 0;
    width: 100%;
    text-align: center;
    margin-top: 0.25rem;
  }
}
</style>

<!-- Non-scoped styles to override Buefy button defaults in the player controls -->
<style>
/* Media Player Transport Controls - Force visible icons and text */
.media-player .custom-controls .button.is-dark {
  background-color: rgba(50, 50, 50, 0.95) !important;
  border-color: rgba(100, 100, 100, 0.8) !important;
  color: #ffffff !important;
}

.media-player .custom-controls .button.is-dark:hover {
  background-color: rgba(80, 80, 80, 1) !important;
  border-color: rgba(120, 120, 120, 0.9) !important;
}

.media-player .custom-controls .button .icon {
  color: #ffffff !important;
}

.media-player .custom-controls .button .icon i,
.media-player .custom-controls .button .icon i.mdi {
  color: #ffffff !important;
  font-size: 1.1rem !important;
}

.media-player .custom-controls .btn-label {
  color: #ffffff !important;
  font-size: 0.7rem !important;
  font-weight: 600 !important;
  margin-left: 3px !important;
}

/* Timecode display visibility */
.media-player .timecode-display {
  color: #ffffff !important;
}

.media-player .timecode-current {
  color: #4de6d0 !important;
  text-shadow: 0 0 8px rgba(0, 209, 178, 0.6) !important;
}

.media-player .timecode-duration {
  color: #ffffff !important;
}
</style>
